import { Component, inject, OnInit } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { CustomerService } from './services/customer.service';
import { PagePayload } from '../shared/models/common.model';
import { catchError, debounceTime, distinctUntilChanged, EMPTY, take, tap } from 'rxjs';
import { Page } from '../shared/models/page';
import { Customer } from './models/customer.model';
import { TableModule } from 'primeng/table';
import { PaginatorModule, PaginatorState } from 'primeng/paginator';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { EmptyStateComponent } from '../shared/components/empty-state/empty-state.component';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmationService } from 'primeng/api';
import { ConfirmPopupModule } from 'primeng/confirmpopup';
import { DecimalPipe } from '@angular/common';

@Component({
    selector: 'app-customers',
    imports: [
        ButtonModule,
        InputTextModule,
        TableModule,
        PaginatorModule,
        ReactiveFormsModule,
        RouterLink,
        EmptyStateComponent,
        TooltipModule,
        ConfirmPopupModule,
        DecimalPipe,
    ],
    providers: [ConfirmationService],
    templateUrl: './customers.component.html',
    styleUrl: './customers.component.scss',
})
export class CustomersComponent implements OnInit {
    customerService: CustomerService = inject(CustomerService);
    confirmationService: ConfirmationService = inject(ConfirmationService);
    urlName = '/customers';
    limit = 10;
    offset = 0;
    customers: Customer[] = [];
    totalCustomers = 0;
    perPage = 10;
    loading = true;
    customerSearch: FormControl<string> = new FormControl<string>('');
    tableMinHeight: string = '0px';
    ngOnInit() {
        this.loadCustomers();
        this.searchForCustomers();
    }

    loadCustomers(query?: string): void {
        this.loading = true;
        const customersPayload: PagePayload = {
            query,
            limit: this.limit,
            offset: this.offset,
        };
        this.customerService
            .getCustomers(customersPayload)
            .pipe(
                take(1),
                tap((customers: Page<Customer>) => {
                    this.loading = false;
                    this.customers = customers.result;
                    this.totalCustomers = customers.total;
                    this.handleTableHeight();
                }),
                catchError(() => {
                    this.loading = false;
                    return EMPTY;
                }),
            )
            .subscribe();
    }

    handleTableHeight(): void {
        if (this.customers.length) {
            this.tableMinHeight = 'auto';
        } else {
            this.tableMinHeight = '50vh';
        }
    }

    onPageChange($event: PaginatorState) {
        this.offset = $event.first || 0;
        this.limit = $event.rows || 10;

        this.loadCustomers(this.customerSearch.value);
    }

    searchForCustomers(): void {
        this.customerSearch.valueChanges
            .pipe(
                distinctUntilChanged(),
                debounceTime(300),
                tap((searchValue: string) => {
                    this.loadCustomers(searchValue);
                }),
            )
            .subscribe();
    }

    onCustomerDelete($event: MouseEvent, customer: Customer) {
        this.confirmationService.confirm({
            target: $event?.target,
            message: `هل انت متأكد من انك تريد مسح العميل:  ${customer.name} ؟`,
            dismissableMask: true,
            acceptLabel: 'نعم',
            rejectLabel: 'لا',
            acceptButtonStyleClass: 'p-button-outlined mx-2',
            rejectButtonStyleClass: 'p-button-danger p-button-outlined mx-2',
            accept: () => {
                this.customerService
                    .deleteCustomer(customer.id)
                    .pipe(
                        take(1),
                        tap(() => {
                            this.loadCustomers(this.customerSearch.value);
                        }),
                    )
                    .subscribe();
            },
        });
    }
}
